import { apiRequest, api } from '../lib/api-client';
import type { LoginResponse, LoginStep1Response, User } from '../types/api';

export async function loginRequest(
  identity: string,
  password: string,
): Promise<LoginStep1Response> {
  return apiRequest<LoginStep1Response>('/auth/login', {
    method: 'POST',
    body: { identity, password },
    skipAuthRefresh: true,
  });
}

export async function loginMfaRequest(
  mfa_token: string,
  code: string,
): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/auth/login/mfa', {
    method: 'POST',
    body: { mfa_token, code },
    skipAuthRefresh: true,
  });
}

export interface EnrollMfaResponse {
  otpauth_url: string;
  qr_data_url: string;
}
export async function enrollMfa(): Promise<EnrollMfaResponse> {
  return api.post<EnrollMfaResponse>('/auth/mfa/enroll', {});
}

export async function confirmMfa(code: string): Promise<{ recovery_codes: string[] }> {
  return api.post<{ recovery_codes: string[] }>('/auth/mfa/confirm', { code });
}

export async function disableMfaRequest(password: string, code: string): Promise<void> {
  await api.post('/auth/mfa/disable', { password, code });
}

export async function regenerateRecoveryCodes(
  password: string,
  code: string,
): Promise<{ recovery_codes: string[] }> {
  return api.post<{ recovery_codes: string[] }>('/auth/mfa/recovery-codes/regenerate', {
    password,
    code,
  });
}

export interface EnrollWithTokenResponse {
  otpauth_url: string;
  qr_data_url: string;
}

/** Self-serve MFA enrollment from the login page (no JWT yet). */
export async function enrollMfaWithToken(
  enrollment_token: string,
): Promise<EnrollWithTokenResponse> {
  return apiRequest<EnrollWithTokenResponse>('/auth/mfa/enroll-with-token', {
    method: 'POST',
    body: { enrollment_token },
    skipAuthRefresh: true,
  });
}

/**
 * Verify the first TOTP code, enable MFA, and complete the login. The
 * response carries the access token + user (so the caller can populate the
 * auth store) plus the one-time recovery codes to surface to the user.
 */
export async function confirmMfaWithToken(
  enrollment_token: string,
  code: string,
): Promise<LoginResponse & { recovery_codes: string[] }> {
  return apiRequest<LoginResponse & { recovery_codes: string[] }>(
    '/auth/mfa/confirm-with-token',
    {
      method: 'POST',
      body: { enrollment_token, code },
      skipAuthRefresh: true,
    },
  );
}

export async function logoutRequest(): Promise<void> {
  await apiRequest('/auth/logout', { method: 'POST', skipAuthRefresh: true });
}

export async function fetchMe(): Promise<User> {
  const res = await api.get<{ user: User }>('/auth/me');
  return res.user;
}

export async function changePasswordRequest(
  current_password: string,
  new_password: string,
): Promise<void> {
  await api.post('/auth/change-password', { current_password, new_password });
}

/**
 * Public — no-enumeration: always returns ok regardless of whether the email
 * matches a real account. A reset link is delivered only if it does.
 */
export async function forgotPasswordRequest(email: string): Promise<void> {
  await apiRequest('/auth/forgot-password', {
    method: 'POST',
    body: { email },
    skipAuthRefresh: true,
  });
}

/** Public — redeems a password-reset token + sets a new password. */
export async function resetPasswordRequest(
  token: string,
  new_password: string,
): Promise<void> {
  await apiRequest('/auth/reset-password', {
    method: 'POST',
    body: { token, new_password },
    skipAuthRefresh: true,
  });
}

/**
 * Public — redeems an email-verification token. If the token's email differs
 * from the user's current address, the API treats it as a change-email
 * confirmation and updates users.email; the response has `changed: true`.
 */
export async function verifyEmailRequest(
  token: string,
): Promise<{ ok: true; changed?: boolean }> {
  return apiRequest<{ ok: true; changed?: boolean }>('/auth/verify-email', {
    method: 'POST',
    body: { token },
    skipAuthRefresh: true,
  });
}

/**
 * Attempts to silently restore a session by calling /auth/refresh. The httpOnly
 * cookie is sent automatically if present. Returns the fresh access token on
 * success, null on failure.
 */
export async function silentRefresh(): Promise<string | null> {
  try {
    const res = await apiRequest<{ access_token: string }>('/auth/refresh', {
      method: 'POST',
      skipAuthRefresh: true,
    });
    return res.access_token;
  } catch {
    return null;
  }
}
